/**
 * Estado e ações da etapa de onboarding do funil.
 */
import { useEffect, useMemo, useState } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import { notify } from '../../../utils/toast';
import { useFunnelForm } from '../../settings/funnel/hooks/useFunnelForm';
import {
    deleteRemovedStages,
    persistStages,
    refreshFunnelData,
    resolvePersistedOrTempId,
} from '../../settings/funnel/hooks/funnelPersist';
import { markFunnelCustomized } from './useOnboardingProgress';
import { markOnboardingStepSkipped } from './useOnboardingSkip';

export function useFunnelOnboardingStep() {
    const { currentOrganization } = useOrganization();
    const orgId = currentOrganization?.id;
    const {
        funnels,
        selectedFunnelId,
        setSelectedFunnelId,
        funnelStages,
        updateFunnel,
        saveFunnelStage,
        deleteFunnelStage,
        refreshStages,
        refreshLeads,
        refreshFunnels,
    } = useLeads();

    const funnelId = selectedFunnelId ?? funnels[0]?.id;
    const currentFunnel = useMemo(
        () => funnels.find((f) => f.id === funnelId) ?? null,
        [funnels, funnelId],
    );
    const stages = useMemo(
        () => (funnelId ? funnelStages.filter((s) => !s.funnelId || s.funnelId === funnelId) : []),
        [funnelStages, funnelId],
    );

    const [isEditing, setIsEditing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [skipping, setSkipping] = useState(false);
    const [loadingStages, setLoadingStages] = useState(false);
    const form = useFunnelForm({ funnel: currentFunnel, stages });

    useEffect(() => {
        if (funnelId && funnelId !== selectedFunnelId) setSelectedFunnelId(funnelId);
    }, [funnelId, selectedFunnelId, setSelectedFunnelId]);

    useEffect(() => {
        if (!funnelId) return;
        let cancelled = false;
        setLoadingStages(true);
        void refreshStages(funnelId).finally(() => {
            if (!cancelled) setLoadingStages(false);
        });
        return () => {
            cancelled = true;
        };
    }, [funnelId, refreshStages]);

    const markDone = async () => {
        if (!orgId) return false;
        return markFunnelCustomized(orgId);
    };

    const onConfirm = async () => {
        if (!orgId || !funnelId || busy) return;
        setBusy(true);
        try {
            if (!(await markDone())) {
                notify.error('Não foi possível confirmar as etapas. Tente novamente.');
            }
        } finally {
            setBusy(false);
        }
    };

    const onSave = async () => {
        if (!orgId || !funnelId || busy) return;
        if (!form.isValid()) {
            notify.error(form.getErrors()[0] || 'Dados inválidos.');
            return;
        }

        setBusy(true);
        try {
            const data = form.getFormData();
            await updateFunnel(funnelId, data.name);
            // Onboarding: remove só etapas do funil atual (lista já filtrada)
            await deleteRemovedStages(deleteFunnelStage, stages, data.stages);
            // Temp id com order — mesmo padrão anterior (`temp_${Date.now()}_${order}`)
            await persistStages(saveFunnelStage, funnelId, data.stages, resolvePersistedOrTempId);
            await refreshFunnelData(refreshStages, refreshLeads, funnelId);
            await refreshFunnels();
            setIsEditing(false);
            notify.success('Funil atualizado.');
        } catch (e) {
            console.error('[Onboarding] Failed to save funnel:', e);
            notify.error('Erro ao salvar funil. Tente novamente.');
        } finally {
            setBusy(false);
        }
    };

    const onSkip = async () => {
        if (!orgId || skipping) return;
        setSkipping(true);
        try {
            if (!(await markOnboardingStepSkipped(orgId, 'funnelSkipped'))) {
                notify.error('Não foi possível pular esta etapa. Tente novamente.');
            }
        } finally {
            setSkipping(false);
        }
    };

    return {
        funnelId,
        stages,
        form,
        isEditing,
        setIsEditing,
        busy,
        skipping,
        loadingStages,
        onConfirm,
        onSave,
        onSkip,
    };
}
